import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";

// SALAR Technologies — AI Fabric Readiness & Infrastructure Assurance
// /ai-fabric-assurance route. Wraps inside global Header/Footer.

const COLORS = {
  navy: "#0A1628",                        // Primary section bg
  deepBlue: "#0F1A2B",                    // Alternating section bg
  salarBlue: "#1B6EC2",
  accent: "#00D4FF",
  gold: "#D4A843",
  white: "#F8FAFB",
  // Dark-theme card surfaces
  cardBg: "#1A2744",                      // Solid dark navy card surface (per design spec)
  cardBgAlt: "#0F1A2B",                   // Alternate section bg (matches deepBlue)
  cardBorder: "rgba(96, 165, 250, 0.25)", // blue-400/25 border
  textPrimary: "#E0F2FE",                 // sky-100 for headings on dark
  textBody: "#BFDBFE",                    // blue-200 for body
  textMuted: "#93C5FD",                   // blue-300 for labels
  gray200: "#CBD5E1",                     // body copy on dark gradient
  red: "#F87171",
  amber: "#FBBF24",
  green: "#4ADE80",
};

// Mandatory dark overlay for any background image on this page.
const IMG_OVERLAY = "linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85))";

const QRPlaceholder = ({ url, size = 80 }) => (
  <div style={{
    width: size, height: size, border: `2px solid ${COLORS.salarBlue}`,
    display: "grid", placeItems: "center", background: COLORS.cardBg,
    fontSize: 8, color: COLORS.textMuted, textAlign: "center", padding: 4,
  }}>
    <div>
      <div style={{ fontWeight: 700, fontSize: 10 }}>QR</div>
      <div style={{ fontSize: 7, marginTop: 2, wordBreak: "break-all" }}>{url}</div>
    </div>
  </div>
);

const BarcodePlaceholder = ({ docId }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 6,
    padding: "4px 8px", border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: 4, background: COLORS.cardBg, fontSize: 9,
    fontFamily: "monospace", color: COLORS.textBody, letterSpacing: 1,
  }}>
    <span>|||||||</span>
    <span>{docId}</span>
    <span>|||||||</span>
  </div>
);

const SectionTag = ({ children }) => (
  <div style={{
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 14px", borderRadius: 20,
    background: `${COLORS.salarBlue}25`, color: COLORS.accent,
    fontSize: 12, fontWeight: 600, letterSpacing: 1.5,
    textTransform: "uppercase", marginBottom: 12,
  }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent }} />
    {children}
  </div>
);

const RiskDot = ({ level }) => {
  const c = level === "high" ? COLORS.red : level === "medium" ? COLORS.amber : COLORS.green;
  return <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: c, marginRight: 6 }} />;
};

const DownloadCard = ({ title, docId, desc, pages, icon, file }) => (
  <a
    href={file}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackDownload(file.split("/").pop().replace(/\.pdf$/, ""), file)}
    data-testid={`aif-download-${docId}`}
    style={{
      background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`,
      borderRadius: 10, padding: "20px 22px", display: "flex", flexDirection: "column",
      gap: 10, transition: "box-shadow 0.2s, border-color 0.2s",
      cursor: "pointer", position: "relative", overflow: "hidden",
      textDecoration: "none",
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(27,110,194,0.25)"; e.currentTarget.style.borderColor = COLORS.salarBlue; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = COLORS.cardBorder; }}
  >
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${COLORS.salarBlue}, ${COLORS.accent})` }} />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div style={{ fontSize: 28 }}>{icon}</div>
      <QRPlaceholder url={`salartechnologies.com/pdf/${docId}`} size={44} />
    </div>
    <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.white }}>{title}</div>
    <div style={{ fontSize: 13, color: COLORS.textBody, lineHeight: 1.5 }}>{desc}</div>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "auto" }}>
      <BarcodePlaceholder docId={docId} />
      <span style={{ fontSize: 11, color: COLORS.textMuted }}>{pages} pages · PDF</span>
    </div>
  </a>
);

const PatentRow = ({ id, title, relevance }) => (
  <div style={{
    display: "grid", gridTemplateColumns: "90px 1fr 1fr",
    gap: 16, padding: "14px 0",
    borderBottom: `1px solid ${COLORS.cardBorder}`, fontSize: 13,
  }}>
    <div style={{ fontWeight: 700, color: COLORS.accent, fontFamily: "monospace" }}>{id}</div>
    <div style={{ color: COLORS.white, fontWeight: 500 }}>{title}</div>
    <div style={{ color: COLORS.textBody, lineHeight: 1.5 }}>{relevance}</div>
  </div>
);

const RiskRow = ({ node, category, geo, crit, repl }) => (
  <div style={{
    display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 80px 80px",
    gap: 10, padding: "12px 0",
    borderBottom: `1px solid ${COLORS.cardBorder}`, fontSize: 13, alignItems: "center",
  }}>
    <div style={{ fontWeight: 600, color: COLORS.white }}>{node}</div>
    <div style={{ color: COLORS.textBody }}>{category}</div>
    <div style={{ color: COLORS.textMuted }}>{geo}</div>
    <div><RiskDot level={crit} />{crit}</div>
    <div><RiskDot level={repl} />{repl}</div>
  </div>
);

export default function AIFabricAssurance() {
  const [activeTab, setActiveTab] = useState("overview");
  const heroRef = useRef(null);

  const tabs = [
    { id: "overview", label: "Assessment Overview" },
    { id: "technical", label: "Technical Architecture" },
    { id: "patents", label: "Patent Portfolio" },
    { id: "supply", label: "Supply Chain Risk" },
    { id: "downloads", label: "Downloads & Verification" },
  ];

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", color: COLORS.textPrimary, background: COLORS.navy, minHeight: "100vh" }}>
      <Helmet>
        <title>AI Fabric Reliability &amp; Ethernet Infrastructure Assurance | SALAR Technologies</title>
        <meta name="description" content="SALAR Technologies provides independent AI fabric readiness, Ethernet-for-AI, InfiniBand, RoCE, multi-lane SoC connectivity, reliability, interoperability, and supply-chain assurance support." />
        <meta property="og:title" content="AI Fabric Reliability & Ethernet Infrastructure Assurance" />
        <meta property="og:description" content="Independent decision support for InfiniBand, Ethernet-for-AI, RoCE, Spectrum-X, UEC-aligned architectures, and multi-lane SoC connectivity environments." />
        <meta property="og:url" content="https://www.salartechnologies.com/ai-fabric-assurance" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/ai-fabric-assurance" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* HERO */}
      <header ref={heroRef} style={{
        backgroundImage: `${IMG_OVERLAY}, url('/img/ai_fabric_hero.jpg')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "80px 24px 60px", position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0, opacity: 0.04,
          backgroundImage: `linear-gradient(${COLORS.accent} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.accent} 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }} />
        <div style={{ maxWidth: 1120, margin: "0 auto", position: "relative" }}>
          <SectionTag>AI Infrastructure Assurance</SectionTag>
          <h1 style={{ fontSize: 42, fontWeight: 800, color: COLORS.white, margin: "16px 0 20px", lineHeight: 1.15, maxWidth: 760 }}>
            AI Fabric Reliability<br />
            <span style={{ color: COLORS.accent }}>&amp; Ethernet Infrastructure Assurance</span>
          </h1>
          <p style={{ fontSize: 17, color: COLORS.gray200, lineHeight: 1.7, maxWidth: 720, margin: "0 0 32px" }}>
            Independent decision support for InfiniBand, Ethernet-for-AI, RDMA over Converged Ethernet (RoCE),
            NVIDIA Spectrum-X, and Ultra Ethernet Consortium (UEC)-aligned fabric architectures and
            multi-lane SoC connectivity environments.
          </p>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <a
              href="mailto:vikrant@salartechnologies.com?subject=AI%20Fabric%20Readiness%20Assessment%20Request"
              data-testid="aif-cta-readiness"
              style={{
                padding: "14px 28px", background: COLORS.salarBlue, color: COLORS.white,
                border: "none", borderRadius: 6, fontWeight: 700, fontSize: 14,
                cursor: "pointer", letterSpacing: 0.5, textDecoration: "none",
              }}
            >
              Request an AI Fabric Readiness Review
            </a>
            <button
              onClick={() => {
                setActiveTab("patents");
                // Smooth scroll to the tab bar so user immediately sees the switched content
                setTimeout(() => {
                  document.querySelector('[data-testid="aif-tab-patents"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 50);
              }}
              data-testid="aif-cta-patents"
              style={{
                padding: "14px 28px", background: "transparent",
                color: COLORS.accent, border: `1px solid ${COLORS.accent}40`,
                borderRadius: 6, fontWeight: 600, fontSize: 14,
                cursor: "pointer",
              }}
            >
              View Patent-Aligned Capability
            </button>
          </div>

          {/* Trust markers */}
          <div style={{ display: "flex", gap: 32, marginTop: 48, flexWrap: "wrap" }}>
            {[
              { label: "USPTO Patents", value: "7 Pending" },
              { label: "NAICS Primary", value: "541330" },
              { label: "CAGE Code", value: "1AXR9" },
              { label: "UEI", value: "U4TVNBSBUJP3" },
              { label: "SAM Status", value: "Active" },
            ].map((t, i) => (
              <div key={i} style={{ borderLeft: `2px solid ${COLORS.salarBlue}`, paddingLeft: 12 }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.5 }}>{t.label}</div>
                <div style={{ fontSize: 16, color: COLORS.white, fontWeight: 700, marginTop: 2, fontFamily: "monospace" }}>{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* TAB BAR */}
      <div style={{ background: COLORS.deepBlue, borderBottom: `1px solid ${COLORS.cardBorder}`, position: "sticky", top: 80, zIndex: 40 }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 24px", display: "flex", gap: 0, overflowX: "auto" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`aif-tab-${t.id}`} style={{
              padding: "14px 20px", background: "none", border: "none",
              borderBottom: activeTab === t.id ? `3px solid ${COLORS.accent}` : "3px solid transparent",
              color: activeTab === t.id ? COLORS.accent : COLORS.textBody,
              fontWeight: activeTab === t.id ? 700 : 500, fontSize: 13,
              cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s",
            }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENT */}
      <main style={{ maxWidth: 1120, margin: "0 auto", padding: "40px 24px 80px" }}>

        {activeTab === "overview" && (
          <div>
            <SectionTag>What We Do</SectionTag>
            <h2 style={{ fontSize: 28, fontWeight: 700, margin: "8px 0 20px" }}>Engineering Decision Layer for AI Networks</h2>
            <p style={{ fontSize: 15, color: COLORS.textBody, lineHeight: 1.8, maxWidth: 800, marginBottom: 40 }}>
              SALAR does not sell switches, GPUs, NICs, or optics. We provide an independent engineering
              assessment layer that sits between your workload requirements and your fabric vendor stack.
              The output is a decision-ready technical package — not a sales pitch for any hardware platform.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 48 }}>
              {[
                { title: "Fabric Readiness Scoring", img: "/img/pexels_fiber_cables.jpg", detail: "Structured evaluation of InfiniBand vs. Ethernet-for-AI fit. Covers RDMA transport model (RoCE v2 vs. native IB), lossless fabric requirements (PFC, ECN, DCQCN), adaptive routing topology, telemetry infrastructure maturity, and operational skill-base alignment.", metric: "12-dimension readiness matrix" },
                { title: "Profile-Based Ethernet Governance", img: "/img/server_room_close.jpg", detail: "Reusable connectivity profiles that encode bandwidth allocation, lane aggregation (multi-lane SoC), failover expectations, QoS class mapping, interoperability assumptions, and validation intent. Anchored to U.S. Patent App. No. 19/433,978.", metric: "Patent-informed methodology" },
                { title: "Supply-Chain Dependency Graph", img: "/img/circuit_chip.jpg", detail: "Maps hidden dependencies across NIC vendors, switch ASICs, transceiver optics, cable assemblies, OSAT packaging, substrate suppliers, firmware supply, and logistics providers. Scores criticality, replaceability, geographic concentration, and provenance confidence.", metric: "Graph-based risk scoring (P3)" },
                { title: "Board & Interconnect Reliability", img: "/img/pexels_server_room.jpg", detail: "Thermal cycling, mechanical stress, solder joint fatigue, via reliability, substrate delamination, and interconnect impedance drift assessment for AI accelerator boards running sustained high-power workloads in dense rack configurations.", metric: "Failure mode risk register" },
              ].map((cap, i) => (
                <div
                  key={i}
                  data-testid={`aif-cap-card-${i}`}
                  style={{
                    backgroundImage: `${IMG_OVERLAY}, url('${cap.img}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    borderRadius: 10,
                    padding: "28px 24px",
                    border: `1px solid ${COLORS.cardBorder}`,
                    minHeight: 240,
                  }}
                >
                  <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 10 }}>{cap.metric}</div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 10px", color: COLORS.white }}>{cap.title}</h3>
                  <p style={{ fontSize: 13, color: COLORS.textBody, lineHeight: 1.65, margin: 0 }}>{cap.detail}</p>
                </div>
              ))}
            </div>

            <div style={{ background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.deepBlue})`, borderRadius: 10, padding: "32px 28px", color: COLORS.white }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Pilot Assessment — 2 to 3 Weeks</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
                {[
                  { phase: "Week 1", title: "Intake & Baseline", items: "Workload classes, GPU/XPU scale, topology diagram, NIC/switch/optics vendor stack, deployment schedule, compliance constraints." },
                  { phase: "Week 1-2", title: "Technical Review", items: "RoCE/PFC/ECN configuration audit, adaptive routing assessment, telemetry infrastructure, firmware lifecycle, benchmark gap analysis." },
                  { phase: "Week 2", title: "Supply Graph Build", items: "NIC, switch ASIC, transceiver, cable, OSAT, substrate, firmware, logistics node mapping. Criticality and replaceability scoring." },
                  { phase: "Week 2-3", title: "Decision Package", items: "Executive decision memo, fabric fit matrix, risk heatmap, supply dependency map, profile governance checklist, mitigation roadmap." },
                ].map((p, i) => (
                  <div key={i} style={{ borderLeft: `2px solid ${COLORS.accent}`, paddingLeft: 16 }}>
                    <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>{p.phase}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, margin: "6px 0 8px" }}>{p.title}</div>
                    <div style={{ fontSize: 12, color: COLORS.gray200, lineHeight: 1.6 }}>{p.items}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "technical" && (
          <div>
            <SectionTag>Technical Architecture</SectionTag>
            <h2 style={{ fontSize: 28, fontWeight: 700, margin: "8px 0 20px" }}>AI Fabric Assessment Methodology</h2>
            <div style={{ background: COLORS.cardBg, borderRadius: 10, border: `1px solid ${COLORS.cardBorder}`, padding: "32px 28px", marginBottom: 28 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Readiness Scoring Dimensions</h3>
              <p style={{ fontSize: 13, color: COLORS.textBody, lineHeight: 1.7, marginBottom: 20 }}>
                Each dimension is scored on a 1-5 maturity scale. The composite readiness score weights dimensions by workload sensitivity. Training-heavy clusters weight transport latency and lossless fabric higher. Inference-heavy clusters weight multi-tenancy and operational flexibility higher.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: 16 }}>
                {[
                  { dim: "D1 — Transport Model Fit", desc: "RDMA verb semantics (native IB vs RoCE v2), message rate capability, queue pair scalability, memory registration overhead." },
                  { dim: "D2 — Lossless Fabric Readiness", desc: "Priority Flow Control (PFC) configuration, ECN marking thresholds, DCQCN parameter tuning, PFC storm detection and mitigation." },
                  { dim: "D3 — Topology Stress Tolerance", desc: "Spine-leaf ratio, oversubscription analysis, rail-optimized vs full-bisection, east-west traffic modeling under collective operations." },
                  { dim: "D4 — Adaptive Routing Maturity", desc: "Switch-level load balancing, packet reordering tolerance, flow-level vs packet-level routing, multipath ECMP hash collision analysis." },
                  { dim: "D5 — Telemetry Infrastructure", desc: "In-band network telemetry (INT), streaming telemetry export, congestion event correlation, NIC-level counter availability." },
                  { dim: "D6 — Firmware & Lifecycle", desc: "NIC firmware version alignment, switch NOS patching cadence, optics firmware qualification, end-of-life tracking across vendor stack." },
                  { dim: "D7 — Multi-Tenant Isolation", desc: "Network slicing mechanism (PKey vs VXLAN/EVPN), QoS enforcement granularity, bandwidth guarantee per tenant/workload class." },
                  { dim: "D8 — Profile Governance (P2)", desc: "Reusable connectivity profile definitions, workload-to-profile mapping, failover expectation encoding, validation intent binding." },
                  { dim: "D9 — Optics & Cable Risk", desc: "Transceiver vendor diversity, connector type (OSFP, QSFP-DD), active vs passive cable mix, thermal derating under sustained load." },
                  { dim: "D10 — Supply Concentration (P3)", desc: "Single-source ASIC dependencies, geographic supplier concentration, OSAT packaging bottlenecks, alternate-source lead times." },
                  { dim: "D11 — Operational Skill Base", desc: "Team proficiency in lossless fabric debugging, RDMA performance tooling, congestion root-cause analysis, vendor escalation readiness." },
                  { dim: "D12 — Compliance & Assurance", desc: "Government/defense fabric requirements, CMMC alignment, supply chain provenance documentation, audit trail completeness." },
                ].map((d, i) => (
                  <div key={i} style={{ padding: "16px 18px", background: i % 2 === 0 ? "rgba(15, 35, 60, 0.30)" : COLORS.cardBg, borderRadius: 8, border: `1px solid ${COLORS.cardBorder}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.accent, marginBottom: 6 }}>{d.dim}</div>
                    <div style={{ fontSize: 12, color: COLORS.textBody, lineHeight: 1.6 }}>{d.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: `${COLORS.red}08`, borderRadius: 10, border: `1px solid ${COLORS.red}20`, padding: "28px 28px" }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.red, marginBottom: 16 }}>Critical Risk Scenarios We Assess</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                {[
                  { risk: "PFC Storm Propagation", desc: "Misconfigured PFC in RoCE fabrics can create network-wide pause frame storms, collapsing throughput across unrelated tenants. We map PFC domains, test deadlock scenarios, and design isolation boundaries." },
                  { risk: "Single-Vendor ASIC Lock", desc: "When your entire switching fabric depends on one ASIC vendor's roadmap, any delay or architectural shift cascades. We score ASIC dependency, identify alternate-qualified platforms, and model transition cost." },
                  { risk: "Optics Thermal Derating", desc: "400G/800G transceivers in dense GPU racks can hit thermal limits, causing link flaps or reduced bandwidth. We assess optics placement, airflow paths, and thermal headroom across your rack design." },
                  { risk: "Firmware Version Drift", desc: "NIC, switch, and optics firmware versions that drift out of vendor-qualified combinations create silent interop failures. We track version matrices and qualification windows." },
                ].map((r, i) => (
                  <div key={i} style={{ background: COLORS.cardBg, borderRadius: 8, padding: "18px 20px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.white, marginBottom: 8 }}>{r.risk}</div>
                    <div style={{ fontSize: 12, color: COLORS.textBody, lineHeight: 1.6 }}>{r.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "patents" && (
          <div>
            <SectionTag>Intellectual Property</SectionTag>
            <h2 style={{ fontSize: 28, fontWeight: 700, margin: "8px 0 20px" }}>Patent-Informed Assessment Methodology</h2>
            <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.7, maxWidth: 800, marginBottom: 32 }}>
              SALAR's assessment methodology is anchored to eight USPTO patent filings spanning board-level reliability, supply chain intelligence, manufacturing stability, process assurance, AI infrastructure power/thermal orchestration, post-quantum trust provenance, and agentic decision governance. The AI fabric lane extends Patent P2 and P3 directly into network infrastructure decision support, Patent P6 extends assurance into the physical rack, cooling loop, and workload-placement layer, Patent P7 extends trust evidence from legacy classical roots into post-quantum-verifiable receipts, and Patent P8 extends decision accountability into agentic workflow governance.
            </p>
            <div style={{ background: COLORS.cardBg, borderRadius: 10, border: `1px solid ${COLORS.cardBorder}`, padding: "28px 28px", marginBottom: 28 }}>
              <div style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr", gap: 16, padding: "12px 0", borderBottom: `2px solid ${COLORS.accent}`, fontWeight: 700, fontSize: 12, color: COLORS.white }}>
                <div>Patent ID</div><div>Title / Filing</div><div>AI Fabric Relevance</div>
              </div>
              <PatentRow id="P1" title="Board-Level Reliability Governance (18/311,244)" relevance="Foundation for interconnect reliability assessment: solder joint fatigue models, via reliability, thermal cycling analysis for high-power AI accelerator boards." />
              <PatentRow id="P2" title="Profile-Based Ethernet Connectivity for Multi-Lane SoC Devices (19/433,978)" relevance="Core AI fabric methodology: reusable connectivity profiles encoding bandwidth, lane aggregation, failover, QoS, interoperability assumptions, and validation intent." />
              <PatentRow id="P3" title="Supply-Chain Risk Graph Intelligence (19/535,459)" relevance="Graph-based dependency scoring for NIC, switch, optics, cable, OSAT, substrate, firmware, and logistics nodes. Criticality, replaceability, and provenance confidence." />
              <PatentRow id="P4" title="Closed-Loop Manufacturing Stability (19/534,285)" relevance="Process stability feedback loops applicable to AI infrastructure component manufacturing: yield monitoring, drift detection, corrective action triggers." />
              <PatentRow id="P5" title="AI-Certified Lithography & Process Assurance (19/564,196)" relevance="Process-level quality gates for semiconductor components used in AI accelerator and networking ASICs. Ensures fabrication integrity before deployment." />
            </div>
            <div style={{ background: `${COLORS.salarBlue}10`, borderRadius: 10, border: `1px solid ${COLORS.salarBlue}30`, padding: "28px 28px" }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: COLORS.accent, marginBottom: 16 }}>P2 Deep Dive — Profile-Based Ethernet Governance</h3>
              <p style={{ fontSize: 13, color: COLORS.textBody, lineHeight: 1.7, marginBottom: 20 }}>
                Modern AI clusters are not simple server-to-switch deployments. A 10,000-GPU training cluster involves hundreds of thousands of individual connectivity decisions — lane counts, bandwidth tiers, failover behaviors, QoS mappings, and interoperability assumptions. Making these decisions per-derivative is unsustainable. P2 addresses this by defining reusable connectivity profiles that encode:
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                {[
                  "Bandwidth allocation per workload class",
                  "Multi-lane SoC lane grouping and aggregation rules",
                  "Failover expectation (which failure modes are tolerated)",
                  "QoS class-of-service mapping to fabric priorities",
                  "Interoperability assumptions across multi-vendor stacks",
                  "Validation intent binding (factory, lab, field profiles)",
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, color: COLORS.white }}>
                    <span style={{ color: COLORS.accent, fontWeight: 700, fontSize: 16, lineHeight: 1 }}>→</span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, padding: "14px 18px", background: COLORS.cardBg, borderRadius: 6, border: `1px solid ${COLORS.cardBorder}`, fontSize: 12, color: COLORS.textBody, fontStyle: "italic" }}>
                <strong style={{ color: COLORS.white, fontStyle: "normal" }}>Safe positioning: </strong>SALAR does not claim that any specific vendor implements this patent. The correct external statement is that P2 is relevant to AI infrastructure where Ethernet connectivity behavior must be governed, validated, reproduced, and adapted across multi-lane SoC, NIC, and deployment contexts.
              </div>
            </div>
          </div>
        )}

        {activeTab === "supply" && (
          <div>
            <SectionTag>Supply Chain Intelligence</SectionTag>
            <h2 style={{ fontSize: 28, fontWeight: 700, margin: "8px 0 20px" }}>AI Fabric Dependency Risk Graph</h2>
            <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.7, maxWidth: 800, marginBottom: 32 }}>
              Every AI cluster has a hardware supply chain that extends far below the OEM logo on the box. SALAR maps these dependencies into a scored graph — identifying concentration risk, provenance gaps, and hidden single-source bottlenecks that most fabric evaluations never surface.
            </p>
            <div style={{ background: COLORS.cardBg, borderRadius: 10, border: `1px solid ${COLORS.cardBorder}`, padding: "28px 28px", marginBottom: 28 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>Illustrative Dependency Nodes</h3>
              <div style={{ overflowX: "auto" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 0.8fr 80px 80px", gap: 10, padding: "10px 0", borderBottom: `2px solid ${COLORS.accent}`, fontWeight: 700, fontSize: 11, color: COLORS.white, textTransform: "uppercase", letterSpacing: 1 }}>
                  <div>Node</div><div>Category</div><div>Region</div><div>Criticality</div><div>Replaceability</div>
                </div>
                <RiskRow node="GPU ASIC (compute die)" category="Semiconductor" geo="Taiwan / US" crit="high" repl="high" />
                <RiskRow node="Network switch ASIC" category="Semiconductor" geo="Taiwan" crit="high" repl="medium" />
                <RiskRow node="NIC controller" category="Semiconductor" geo="Israel / US" crit="high" repl="medium" />
                <RiskRow node="800G transceiver optics" category="Photonics" geo="China / US / Japan" crit="medium" repl="low" />
                <RiskRow node="OSAT packaging (CoWoS)" category="Packaging" geo="Taiwan" crit="high" repl="high" />
                <RiskRow node="ABF substrate" category="Materials" geo="Japan" crit="high" repl="high" />
                <RiskRow node="Active copper cable" category="Interconnect" geo="China / Mexico" crit="low" repl="low" />
                <RiskRow node="Switch NOS firmware" category="Software" geo="US / India" crit="medium" repl="medium" />
              </div>
            </div>
          </div>
        )}

        {activeTab === "downloads" && (
          <div>
            <SectionTag>Document Control</SectionTag>
            <h2 style={{ fontSize: 28, fontWeight: 700, margin: "8px 0 20px" }}>Assessment Pack Downloads</h2>
            <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.7, maxWidth: 800, marginBottom: 32 }}>
              All documents are version-controlled, QR-coded for verification, and barcoded with immutable document IDs.
              SHA-256 integrity hashes are computed at publication time. Scan any document QR code to verify authenticity.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18, marginBottom: 40 }}>
              <DownloadCard
                title="AI Fabric Capability Statement"
                docId="SALAR-AIF-CAP-v3"
                desc="Company positioning, core competencies, fabric readiness methodology, and administrative credentials for first-touch outreach."
                pages="3" icon="📋"
                file="/pdf/SALAR_AI_Fabric_Capability_Statement.pdf"
              />
              <DownloadCard
                title="AI Fabric Quad Chart"
                docId="SALAR-AIF-QC-v3"
                desc="One-page technical capture chart: problem statement, SALAR offer, patent-informed technical wedge, pilot output, and engagement phases."
                pages="2" icon="📊"
                file="/pdf/SALAR_AI_Fabric_Quad_Chart.pdf"
              />
              <DownloadCard
                title="P2 Patent Relevance Brief"
                docId="SALAR-P2-BRIEF-v3"
                desc="Profile-Based Ethernet Connectivity for Multi-Lane SoC Devices. AI fabric use cases, safe positioning language, and claim-safe evidence."
                pages="2" icon="📜"
                file="/pdf/SALAR_P2_Patent_Brief.pdf"
              />
              <DownloadCard
                title="AI Fabric Risk Matrix"
                docId="SALAR-AIF-RISKMATRIX-v1"
                desc="Patent-to-criteria mapping with risk scoring methodology. Detailed dimension-level matrix for fabric readiness scoring."
                pages="2" icon="⚠️"
                file="/pdf/SALAR_AI_Fabric_Risk_Matrix.pdf"
              />
            </div>

            {/* Pilot Offer note */}
            <div style={{ padding: "20px 24px", background: `${COLORS.gold}15`, borderLeft: `4px solid ${COLORS.gold}`, borderRadius: 6, marginTop: 24 }}>
              <div style={{ fontSize: 12, color: COLORS.gold, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>
                Pilot Offer · Available on Request
              </div>
              <p style={{ fontSize: 13, color: COLORS.textBody, lineHeight: 1.6, margin: 0 }}>
                The full <strong style={{ color: COLORS.white }}>AI Fabric Readiness Pilot Offer</strong> (scope,
                deliverables, engagement phases, decision package contents) is shared by email after an initial scoping
                conversation. Use the <em>&ldquo;Request an AI Fabric Readiness Review&rdquo;</em> button to start that
                conversation.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* DECISION FRAMEWORK — InfiniBand vs Ethernet Comparison (always visible) */}
      <section
        data-testid="aif-decision-framework"
        style={{ background: COLORS.deepBlue, padding: "64px 24px", borderTop: `1px solid ${COLORS.cardBorder}` }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <SectionTag>Decision Framework</SectionTag>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: COLORS.white, margin: "8px 0 12px", lineHeight: 1.25 }}>
            InfiniBand vs. Ethernet for AI
          </h2>
          <p style={{ fontSize: 15, color: COLORS.textBody, lineHeight: 1.7, maxWidth: 820, margin: "0 0 28px" }}>
            A practical comparison for AI infrastructure decisions. SALAR uses this framework as a starting
            point for fabric readiness scoring &mdash; weighted by your specific workload mix, multi-tenancy
            posture, and supply-chain risk tolerance.
          </p>

          <div
            style={{
              background: COLORS.navy,
              borderRadius: 10,
              border: `1px solid ${COLORS.cardBorder}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
              padding: "20px",
            }}
          >
            <img
              src="/img/SALAR_InfiniBand_vs_Ethernet_Comparison.svg"
              alt="InfiniBand vs Ethernet for AI — practical comparison for AI infrastructure decisions: Lossless transport, RDMA, Vendor ecosystem, Maturity, Best fit, Operations skill, Cost per port"
              data-testid="aif-comparison-graphic"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                maxWidth: 900,
                margin: "0 auto",
              }}
            />
          </div>

          <p style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 18, fontStyle: "italic", maxWidth: 820 }}>
            SALAR is fabric-agnostic. The above is a generalized industry comparison &mdash; final fabric
            recommendations are produced by SALAR&rsquo;s 12-dimension readiness scoring against your specific
            workload, scale, operations maturity, and compliance posture.
          </p>
        </div>
      </section>

      {/* Related Capability — Patent 6 Physical AI Infrastructure Assurance cross-link */}
      <section
        data-testid="aif-related-patent6"
        style={{ background: COLORS.navy, padding: "48px 24px", borderTop: `1px solid ${COLORS.cardBorder}` }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <div
            style={{
              background: COLORS.cardBg,
              borderRadius: 12,
              border: `1px solid ${COLORS.cardBorder}`,
              borderLeft: `4px solid ${COLORS.accent}`,
              padding: "26px 28px",
              boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "center" }} className="md:grid-cols-[1fr_auto] grid-cols-1">
              <div>
                <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
                  Physical AI Infrastructure Assurance · Patent 6 · Patent Pending
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: COLORS.white, margin: "0 0 10px", lineHeight: 1.25 }}>
                  Reliability-Aware Power &amp; Thermal Orchestration
                </h3>
                <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.65, margin: 0, maxWidth: 760 }}>
                  SALAR&rsquo;s Patent 6 extends AI assurance from network and decision architecture into the rack-level
                  power, cooling, workload, and service layer of AI infrastructure. The public-safe concept connects
                  accelerator-board telemetry, rack power, liquid cooling, digital-twin modeling, safety-gated control,
                  and audit evidence so operators can pursue energy savings without creating hidden reliability risk.
                  U.S. Patent Application No. 19/685,128.
                </p>
              </div>
              <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                <a
                  href="/licensing/patent-6-ai-infrastructure"
                  data-testid="aif-patent6-brief-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: COLORS.salarBlue,
                    color: COLORS.white,
                    padding: "12px 22px",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 14,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Patent 6 details &amp; licensing &rarr;
                </a>
                <a
                  href="/patent-portfolio#patent-6"
                  data-testid="aif-patent6-portfolio-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: "transparent",
                    color: COLORS.accent,
                    padding: "8px 14px",
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 13,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    border: `1px solid ${COLORS.accent}`,
                  }}
                >
                  View Patent 6 details
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Mission-Assurance Capability — PCIe 6.0 / PAM4 cross-link card */}
      <section
        data-testid="aif-related-pcie"
        style={{ background: COLORS.navy, padding: "48px 24px", borderTop: `1px solid ${COLORS.cardBorder}` }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <div
            style={{
              background: COLORS.cardBg,
              borderRadius: 12,
              border: `1px solid ${COLORS.cardBorder}`,
              borderLeft: `4px solid ${COLORS.accent}`,
              padding: "26px 28px",
              boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "center" }} className="md:grid-cols-[1fr_auto] grid-cols-1">
              <div>
                <div style={{ fontSize: 10, color: COLORS.accent, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>
                  Related Mission-Assurance Capability
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: COLORS.white, margin: "0 0 10px", lineHeight: 1.25 }}>
                  PCIe 6.0 / PAM4 Link Assurance
                </h3>
                <p style={{ fontSize: 14, color: COLORS.textBody, lineHeight: 1.65, margin: 0, maxWidth: 760 }}>
                  AI fabrics depend on high-speed interconnects whose risk may sit beyond the protocol
                  table: board stackup, connectors, retimers, equalization behavior, BER / FEC evidence,
                  supplier provenance, and manufacturing drift. SALAR&rsquo;s Mission Assurance page now
                  includes a PCIe 6.0 / PAM4 Link Assurance brief for decision-ready evidence review.
                </p>
              </div>
              <div style={{ flexShrink: 0 }}>
                <a
                  href="/mission-assurance#pcie-pam4-link-assurance"
                  data-testid="aif-pcie-related-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: COLORS.salarBlue,
                    color: COLORS.white,
                    padding: "12px 22px",
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 14,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  View PCIe / PAM4 Link Assurance &rarr;
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA — always-visible quick downloads */}
      <section
        data-testid="aif-footer-cta"
        style={{
          backgroundImage: `${IMG_OVERLAY}, url('/img/data_center_servers.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          padding: "60px 24px",
        }}
      >
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <SectionTag>Public Capability Pack</SectionTag>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: COLORS.white, margin: "10px 0 12px" }}>
              Download the AI Fabric Public Capability Pack
            </h2>
            <p style={{ fontSize: 14, color: COLORS.gray200, lineHeight: 1.7, maxWidth: 720, margin: "0 auto" }}>
              Four public-safe documents covering positioning, the patent-informed wedge, and the risk-scoring
              methodology. The full pilot offer is shared by email after a brief scoping conversation.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {[
              { label: "AI Fabric Capability Statement", file: "/pdf/SALAR_AI_Fabric_Capability_Statement.pdf", testid: "footer-dl-capability" },
              { label: "AI Fabric Quad Chart",            file: "/pdf/SALAR_AI_Fabric_Quad_Chart.pdf",          testid: "footer-dl-quadchart" },
              { label: "P2 Patent Relevance Brief",       file: "/pdf/SALAR_P2_Patent_Brief.pdf",               testid: "footer-dl-p2brief" },
              { label: "AI Fabric Risk Matrix",           file: "/pdf/SALAR_AI_Fabric_Risk_Matrix.pdf",         testid: "footer-dl-riskmatrix" },
            ].map((b) => (
              <a
                key={b.testid}
                href={b.file}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDownload(b.file.split("/").pop().replace(/\.pdf$/, ""), b.file)}
                data-testid={b.testid}
                style={{
                  background: COLORS.white,
                  color: COLORS.navy,
                  padding: "16px 18px",
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: 13,
                  textDecoration: "none",
                  textAlign: "center",
                  border: `1px solid rgba(255,255,255,0.1)`,
                  transition: "transform 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,212,255,0.25)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ fontSize: 22, marginBottom: 6, color: COLORS.salarBlue }}>⬇</div>
                {b.label}
                <div style={{ fontSize: 10, color: "#7A8A9C", fontWeight: 500, marginTop: 4 }}>PDF</div>
              </a>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 32 }}>
            <a
              href="mailto:vikrant@salartechnologies.com?subject=AI%20Fabric%20Readiness%20Assessment%20Request"
              data-testid="footer-cta-pilot-email"
              style={{
                display: "inline-block",
                padding: "14px 30px",
                background: COLORS.salarBlue,
                color: COLORS.white,
                textDecoration: "none",
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 14,
                letterSpacing: 0.5,
              }}
            >
              Request the Full Pilot Offer by Email →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
